import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Node, Edge } from "@xyflow/react";
import {
  useCreateAiListingMutation,
  useCreateAiTemplateMutation,
  useGetAiTemplateQuery,
  useGetAiTemplatesQuery,
  useGetDeployedAgentQuery,
  useGetDeployedAgentsQuery,
  useSetTemplatePromptMutation,
  useUpdateAiTemplateMutation,
} from "@/store/admin_api";
import useAssignAgentForm from "@/hooks/useAssignAgentForm";
import type { Prompt } from "@/model/interface/Prompt";
import { apiErrorMessage } from "@/util/apiError";
import CreatePromptDialog from "./components/CreatePromptDialog";
import TemplatePromptSection from "./template-sections/TemplatePromptSection";
import TemplateSkillsToolsSection from "./template-sections/TemplateSkillsToolsSection";
import TemplateVoiceEngineSection from "./template-sections/TemplateVoiceEngineSection";
import TemplateConversationSection from "./template-sections/TemplateConversationSection";
import WizardStepper from "./wizard/WizardStepper";
import WizardFooter from "./wizard/WizardFooter";
import WizardStartStep, { WizardStartMode } from "./wizard/WizardStartStep";
import WizardDeployStep, { EMPTY_LISTING_DRAFT, WizardListingDraft } from "./wizard/WizardDeployStep";
import WizardTestStep from "./wizard/WizardTestStep";
import { DEPLOY_WIZARD_STEP, TEST_WIZARD_STEP, WIZARD_STEPS, WIZARD_STEP_FIELDS } from "./wizard/wizardSteps";
import {
  TEMPLATE_FORM_DEFAULTS,
  TemplateFormValues,
  formValuesToTemplatePayload,
  templateConversationFlow,
  templateSchema,
  templateToFormValues,
} from "./templateForm";

export default function AiAgentWizard() {
  const { templateId } = useParams<{ templateId?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const sourceAgentId = searchParams.get("sourceAgent") ?? "";
  const deployedAgentId = searchParams.get("agent") ?? "";

  const { data: template } = useGetAiTemplateQuery(templateId!, { skip: !templateId });
  const { data: templatePage } = useGetAiTemplatesQuery({ size: 200 });
  const { data: deployedPage } = useGetDeployedAgentsQuery({ page: 0, size: 50 });
  const { data: deployedAgent } = useGetDeployedAgentQuery(deployedAgentId, { skip: !deployedAgentId });

  const [createTemplate, { isLoading: isCreatingTemplate }] = useCreateAiTemplateMutation();
  const [updateTemplate, { isLoading: isUpdatingTemplate }] = useUpdateAiTemplateMutation();
  const [setTemplatePrompt, { isLoading: isSettingPrompt }] = useSetTemplatePromptMutation();
  const [createListing, { isLoading: isCreatingListing }] = useCreateAiListingMutation();

  const [startMode, setStartMode] = useState<WizardStartMode>("new");
  const [existingTemplateId, setExistingTemplateId] = useState("");
  const [copyAgentId, setCopyAgentId] = useState("");
  const [createPromptOpen, setCreatePromptOpen] = useState(false);
  const [flowNodes, setFlowNodes] = useState<Node[]>([]);
  const [flowEdges, setFlowEdges] = useState<Edge[]>([]);
  const [initializedTemplateId, setInitializedTemplateId] = useState<string | null>(null);
  const [listingEnabled, setListingEnabled] = useState(false);
  const [listing, setListing] = useState<WizardListingDraft>(EMPTY_LISTING_DRAFT);

  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: TEMPLATE_FORM_DEFAULTS,
  });
  const assignController = useAssignAgentForm({
    initialTemplateId: templateId,
    initialSourceAgentId: sourceAgentId || undefined,
  });

  const templates = templatePage?.items ?? [];
  const deployedAgents = deployedPage?.items ?? [];
  const isSavingTemplate = isCreatingTemplate || isUpdatingTemplate;
  const isTemplateReady = !templateId || initializedTemplateId === templateId;

  const isStepReachable = (step: number) => {
    if (step === 1) {
      return true;
    }
    if (step <= 5) {
      return Boolean(templateId);
    }
    if (step === DEPLOY_WIZARD_STEP) {
      return Boolean(templateId || sourceAgentId);
    }
    return Boolean(deployedAgentId);
  };

  const highestReachableStepUpTo = (step: number) => {
    let candidate = step;
    while (candidate > 1 && !isStepReachable(candidate)) {
      candidate -= 1;
    }
    return candidate;
  };

  const fallbackStep = templateId ? (template?.defaultPromptVersionId ? 3 : 2) : 1;
  const requestedStep = parseInt(searchParams.get("step") ?? "", 10);
  const targetStep =
    Number.isInteger(requestedStep) && requestedStep >= 1 && requestedStep <= WIZARD_STEPS.length
      ? requestedStep
      : fallbackStep;
  const currentStep = highestReachableStepUpTo(targetStep);
  const activeStep = WIZARD_STEPS[currentStep - 1];

  const goToStep = (step: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("step", String(step));
    setSearchParams(nextParams);
  };

  const saveTemplate = async (): Promise<string | null> => {
    const payload = formValuesToTemplatePayload(form.getValues(), flowNodes, flowEdges);
    try {
      if (!templateId) {
        const created = await createTemplate(payload).unwrap();
        return created.id;
      }
      await updateTemplate({ id: templateId, data: payload }).unwrap();
      return templateId;
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to save template"));
      return null;
    }
  };

  const handleAssignPrompt = async (promptVersionId: string | null) => {
    if (!templateId) {
      return;
    }
    try {
      await setTemplatePrompt({ id: templateId, promptVersionId }).unwrap();
      toast.success("Default prompt updated");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to update prompt"));
    }
  };

  const handlePromptCreated = (created: Prompt) => {
    const firstVersion = created.versions?.[0];
    if (firstVersion) {
      handleAssignPrompt(firstVersion.id);
    }
  };

  const handleFlowChange = (nodes: Node[], edges: Edge[]) => {
    setFlowNodes(nodes);
    setFlowEdges(edges);
  };

  const handleStartNext = async () => {
    if (startMode === "template") {
      if (!existingTemplateId) {
        toast.error("Pick a template to continue");
        return;
      }
      navigate(`/platform-admin/ai-agents/new/${existingTemplateId}?step=${DEPLOY_WIZARD_STEP}`);
      return;
    }
    if (startMode === "agent") {
      if (!copyAgentId) {
        toast.error("Pick an agent to copy");
        return;
      }
      navigate(`/platform-admin/ai-agents/new?step=${DEPLOY_WIZARD_STEP}&sourceAgent=${copyAgentId}`);
      return;
    }
    const isValid = await form.trigger(WIZARD_STEP_FIELDS[1]);
    if (!isValid) {
      return;
    }
    const savedId = await saveTemplate();
    if (!savedId) {
      return;
    }
    toast.success(templateId ? "Draft saved" : "Draft template created");
    navigate(`/platform-admin/ai-agents/new/${savedId}?step=2`, { replace: true });
  };

  const handleTemplateStepNext = async (step: number) => {
    if (step === 2) {
      goToStep(3);
      return;
    }
    const stepFields = WIZARD_STEP_FIELDS[step] ?? [];
    const isValid = stepFields.length === 0 || (await form.trigger(stepFields));
    if (!isValid) {
      return;
    }
    const savedId = await saveTemplate();
    if (!savedId) {
      return;
    }
    goToStep(step + 1);
  };

  const handlePublishAndContinue = async () => {
    const isValid = await form.trigger(WIZARD_STEP_FIELDS[5]);
    if (!isValid) {
      return;
    }
    form.setValue("status", "PUBLISHED");
    const savedId = await saveTemplate();
    if (!savedId) {
      return;
    }
    toast.success("Template published");
    goToStep(DEPLOY_WIZARD_STEP);
  };

  const handleSaveAndExit = async () => {
    const savedId = await saveTemplate();
    if (!savedId) {
      return;
    }
    toast.success("Template saved");
    navigate("/platform-admin/ai-agents/templates");
  };

  const createListingFromDraft = async (): Promise<boolean> => {
    if (!templateId) {
      return false;
    }
    const isPrivateListing = listing.visibility === "PRIVATE";
    if (isPrivateListing && listing.organisations.length === 0) {
      toast.error("Add at least one organisation to the private listing");
      return false;
    }
    try {
      await createListing({
        template: { id: templateId },
        displayName: listing.displayName.trim() || undefined,
        longDescription: "",
        perks: [],
        limits: [],
        media: null,
        visibility: listing.visibility,
        allowedOrganisationIds: isPrivateListing ? listing.organisations.map((organisation) => organisation.id) : [],
        monthlyFee: isPrivateListing ? Number(listing.monthlyFee) || 0 : 0,
        perMinuteFee: 0,
        featured: false,
        sortOrder: 0,
      }).unwrap();
      toast.success(isPrivateListing ? "Private listing created" : "Marketplace listing created");
      return true;
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "The listing could not be created"));
      return false;
    }
  };

  const handleCreateListingOnly = async () => {
    const isCreated = await createListingFromDraft();
    if (isCreated) {
      navigate("/platform-admin/ai-agents/listings");
    }
  };

  const handleDeploy = async () => {
    const createdAgent = await assignController.createAgent();
    if (!createdAgent) {
      return;
    }
    if (listingEnabled) {
      await createListingFromDraft();
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("step", String(TEST_WIZARD_STEP));
    nextParams.set("agent", createdAgent.id);
    setSearchParams(nextParams);
  };

  const handleFinishWithoutDeploying = () => {
    if (templateId) {
      navigate(`/platform-admin/ai-agents/templates/${templateId}`);
      return;
    }
    navigate("/platform-admin/ai-agents/templates");
  };

  const handleFinish = () => {
    if (deployedAgentId) {
      navigate(`/platform-admin/ai-agents/deployed/${deployedAgentId}`);
      return;
    }
    navigate("/platform-admin/ai-agents/deployed");
  };

  const handleBack = () => {
    if (currentStep === DEPLOY_WIZARD_STEP && !templateId) {
      navigate("/platform-admin/ai-agents/new");
      return;
    }
    goToStep(currentStep - 1);
  };

  const footerPropsForStep = () => {
    if (currentStep === 1) {
      const newTemplateLabel = templateId ? "Save and continue" : "Create draft and continue";
      return {
        primaryLabel: startMode === "new" ? newTemplateLabel : "Continue",
        onPrimary: handleStartNext,
        isBusy: isSavingTemplate,
      };
    }
    if (currentStep === 5) {
      return {
        primaryLabel: "Publish and continue",
        onPrimary: handlePublishAndContinue,
        isBusy: isSavingTemplate,
        secondaryLabel: "Save and finish later",
        onSecondary: handleSaveAndExit,
      };
    }
    if (currentStep === DEPLOY_WIZARD_STEP) {
      return {
        primaryLabel: "Deploy agent",
        onPrimary: handleDeploy,
        primaryDisabled: !assignController.canSubmit,
        isBusy: assignController.isCreating || isCreatingListing,
        secondaryLabel: listingEnabled ? "Create listing only" : "Finish without deploying",
        onSecondary: listingEnabled ? handleCreateListingOnly : handleFinishWithoutDeploying,
      };
    }
    if (currentStep === TEST_WIZARD_STEP) {
      return {
        primaryLabel: "Finish",
        onPrimary: handleFinish,
      };
    }
    return {
      primaryLabel: "Next",
      onPrimary: () => handleTemplateStepNext(currentStep),
      isBusy: isSavingTemplate,
    };
  };

  useEffect(() => {
    if (!template || initializedTemplateId === template.id) {
      return;
    }
    form.reset(templateToFormValues(template));
    const flow = templateConversationFlow(template);
    setFlowNodes(flow?.nodes ?? []);
    setFlowEdges(flow?.edges ?? []);
    setInitializedTemplateId(template.id);
  }, [template, initializedTemplateId]);

  useEffect(() => {
    if (templateId) {
      assignController.setField("sourceKind", "template");
      assignController.setField("templateId", templateId);
      return;
    }
    if (sourceAgentId) {
      assignController.setField("sourceKind", "agent");
      assignController.setField("sourceAgentId", sourceAgentId);
    }
  }, [templateId, sourceAgentId]);

  if (!isTemplateReady) {
    return (
      <div className="space-y-6 p-4 text-foreground">
        <h1 className="text-3xl font-bold tracking-tight">New AI agent</h1>
        <Card>
          <CardContent className="py-12">
            <div className="space-y-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-1/3" />
              <div className="h-10 bg-muted rounded" />
              <div className="h-20 bg-muted rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 text-foreground">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/platform-admin/ai-agents")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">New AI agent</h1>
          <p className="text-muted-foreground">
            Step {currentStep} of {WIZARD_STEPS.length}. {activeStep.description}
          </p>
        </div>
      </div>

      <WizardStepper currentStep={currentStep} isStepReachable={isStepReachable} onSelectStep={goToStep} />

      <Form {...form}>
        <div className="space-y-6">
          {currentStep === 1 && (
            <WizardStartStep
              startMode={startMode}
              onStartModeChange={setStartMode}
              existingTemplateId={existingTemplateId}
              onExistingTemplateChange={setExistingTemplateId}
              copyAgentId={copyAgentId}
              onCopyAgentChange={setCopyAgentId}
              templates={templates}
              deployedAgents={deployedAgents}
            />
          )}

          {currentStep === 2 && (
            <TemplatePromptSection
              currentVersionId={template?.defaultPromptVersionId}
              currentVersionLabel={template?.defaultPromptVersionLabel}
              onAssignPrompt={handleAssignPrompt}
              isAssigningPrompt={isSettingPrompt}
              onRequestNewPrompt={() => setCreatePromptOpen(true)}
              savesImmediately
            />
          )}

          {currentStep === 3 && <TemplateSkillsToolsSection />}

          {currentStep === 4 && <TemplateVoiceEngineSection collapseAdvanced />}

          {currentStep === 5 && (
            <>
              <TemplateConversationSection
                flowNodes={flowNodes}
                flowEdges={flowEdges}
                onFlowChange={handleFlowChange}
                showFlowBuilder={false}
                collapseAdvanced
              />
              <p className="text-sm text-muted-foreground">
                The conversation flow builder lives in the{" "}
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-sm"
                  onClick={() => navigate(`/platform-admin/ai-agents/templates/${templateId}`)}
                >
                  full template editor
                </Button>
                . Your flow is kept as it is when you save from here.
              </p>
            </>
          )}

          {currentStep === DEPLOY_WIZARD_STEP && (
            <WizardDeployStep
              controller={assignController}
              canCreateListing={template?.status === "PUBLISHED"}
              listingEnabled={listingEnabled}
              onListingEnabledChange={setListingEnabled}
              listing={listing}
              onListingChange={setListing}
            />
          )}

          {currentStep === TEST_WIZARD_STEP && <WizardTestStep agent={deployedAgent} />}

          <WizardFooter onBack={currentStep === 1 ? undefined : handleBack} {...footerPropsForStep()} />
        </div>
      </Form>

      <CreatePromptDialog
        kind="AGENT"
        open={createPromptOpen}
        onOpenChange={setCreatePromptOpen}
        onCreated={handlePromptCreated}
      />
    </div>
  );
}
